/**
 * Money in the UI: minor-unit strings in, minor-unit strings out. The
 * only parsing is of what a person types, and it never goes through a
 * float. Formatting follows the locale the app sets with setFormatLocale.
 */

let currentLocale = 'en';

/** The locale formatting and parsing use when a call names none. */
export function setFormatLocale(locale: string): void {
  currentLocale = locale;
}

export function formatLocale(): string {
  return currentLocale;
}

/** The decimal mark of a locale: "," in de-DE, "." in en-GB. */
export function decimalMark(locale = currentLocale): string {
  return (
    new Intl.NumberFormat(locale).formatToParts(1.5).find((p) => p.type === 'decimal')?.value ?? '.'
  );
}

/** Minor-unit exponent of a currency per ISO 4217 (EUR 2, JPY 0, BHD 3). */
export function currencyExponent(currency: string): number {
  return (
    new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions()
      .maximumFractionDigits ?? 2
  );
}

/** Format minor units for display: "-123456" EUR in de-DE -> "-1.234,56 €". */
export function formatMoney(
  minor: string,
  currency: string,
  locale = currentLocale,
  exponent = currencyExponent(currency)
): string {
  const value = BigInt(minor);
  const negative = value < 0n;
  const abs = negative ? -value : value;
  const base = 10n ** BigInt(exponent);
  const whole = abs / base;
  const frac = abs % base;
  // Intl formats the integer part (grouping) and the currency; the fraction
  // is attached by hand so no step converts to Number.
  const parts = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: exponent,
    maximumFractionDigits: exponent
  }).formatToParts(0);
  const decimal = parts.find((p) => p.type === 'decimal')?.value ?? ',';
  const grouped = new Intl.NumberFormat(locale, { useGrouping: true }).format(whole);
  const number =
    exponent > 0 ? `${grouped}${decimal}${frac.toString().padStart(exponent, '0')}` : grouped;
  const template = parts
    .map((p) =>
      p.type === 'integer' ? '\u0000' : p.type === 'decimal' || p.type === 'fraction' ? '' : p.value
    )
    .join('');
  const text = template.replace('\u0000', number);
  return negative ? `-${text}` : text;
}

/** Format without currency symbol, for dense tables: "1.234,56". */
export function formatAmount(minor: string, locale = currentLocale, exponent = 2): string {
  const value = BigInt(minor);
  const negative = value < 0n;
  const abs = negative ? -value : value;
  const base = 10n ** BigInt(exponent);
  const grouped = new Intl.NumberFormat(locale, { useGrouping: true }).format(abs / base);
  const decimal = decimalMark(locale);
  const frac = exponent > 0 ? `${decimal}${(abs % base).toString().padStart(exponent, '0')}` : '';
  return `${negative ? '-' : ''}${grouped}${frac}`;
}

export type ParseResult =
  { ok: true; minor: string } | { ok: false; reason: 'empty' | 'invalid' | 'precision' };

/**
 * Parse a typed amount into positive minor units. The locale's decimal
 * mark decides: in de-DE "1.234,56" and "1234,5"; in en "1,234.56" and
 * "1234.5". The other mark is accepted as grouping, and as a decimal mark
 * only where it cannot be grouping ("1234,5" in en). A single grouping
 * mark followed by exactly three digits is grouping ("1.234" in de-DE).
 */
export function parseAmount(input: string, exponent = 2, locale = currentLocale): ParseResult {
  const dec = decimalMark(locale) === ',' ? ',' : '.';
  const grp = dec === ',' ? '.' : ',';
  const s = input.trim().replace(/[\s\u00a0€$£¥]|[A-Z]{3}/g, '');
  if (s === '') return { ok: false, reason: 'empty' };
  const d = dec === ',' ? ',' : '\\.';
  const g = grp === ',' ? ',' : '\\.';
  let integer: string;
  let fraction = '';
  if (s.includes(dec)) {
    const ok = new RegExp(`^\\d{1,3}(${g}\\d{3})*(${d}\\d*)?$|^\\d*${d}\\d+$|^\\d+${d}?$`);
    if (!ok.test(s)) return { ok: false, reason: 'invalid' };
    const [i, f = ''] = s.split(dec);
    integer = (i ?? '').replaceAll(grp, '');
    fraction = f;
  } else if (
    new RegExp(`^\\d+${g}\\d{3}$`).test(s) ||
    new RegExp(`^\\d{1,3}(${g}\\d{3}){2,}$`).test(s)
  ) {
    integer = s.replaceAll(grp, '');
  } else if (new RegExp(`^\\d+(${g}\\d+)?$`).test(s)) {
    const [i, f = ''] = s.split(grp);
    integer = i ?? '';
    fraction = f;
  } else {
    return { ok: false, reason: 'invalid' };
  }
  if (fraction.length > exponent) return { ok: false, reason: 'precision' };
  const minor = BigInt(`${integer === '' ? '0' : integer}${fraction.padEnd(exponent, '0')}`);
  return { ok: true, minor: minor.toString() };
}

export function sumMinor(values: readonly string[]): string {
  return values.reduce((acc, v) => acc + BigInt(v === '' ? '0' : v), 0n).toString();
}
